/**
 * English → Simplified Chinese (简体中文) UI-only dictionary.
 *
 * ONLY translates UI chrome: sidebar menu items, page titles, section
 * headings, table column headers, sort labels, tab names, and standard
 * button labels.
 *
 * NEVER translates: PO numbers, LD-XXXXXX codes, VINs, Rim Sizes,
 * customer names/addresses/emails, product SKUs, order notes, uploaded
 * file names, timestamps, currency values, status codes like "IN_PROD",
 * or any email template content.
 *
 * How to use: import { t } from "@/i18n/translate" — but for translations
 * that need to react to the current language, use the useT() hook from
 * LanguageContext.
 */

// Simple hand-crafted map. Left key must EXACTLY match the English string
// as it appears in the UI (case, spacing, punctuation). Missing entries
// fall through to the original English so nothing ever renders as `null`.
export const ZH_TRANSLATIONS = {
  // -------------- Sidebar / navigation --------------
  "Dashboard": "仪表盘",
  "Customers": "客户",
  "Pre Orders": "预订单",
  "Abandoned Orders": "已放弃订单",
  "Pending Orders": "待处理报价",
  "Advance Orders": "已处理报价",
  "Approved Leads": "已批准线索",
  "NA Orders": "无此型号订单",
  "Flagged Orders": "已标记订单",
  "Cancelled Orders": "已取消订单",
  "No Mold": "无模具",
  "Inv Packing": "库存包装",
  "Inventory Packing": "库存包装",
  "Inventory packing": "库存包装",
  "waiting to be packed": "等待包装",
  "Possible match": "可能匹配",
  "PENDING SHIPPING": "待发货",
  "SHIPPED": "已发货",
  "China Warehouse": "中国仓库",
  "CHINA WAREHOUSE": "中国仓库",
  "TO COLLECT": "待取货",
  "COLLECT FROM STORAGE": "从仓库取货",
  "Collect from China warehouse": "从中国仓库取货",
  "Pick the item from storage and check it matches the catalog number and colour below.": "从仓库取出产品，核对下方的目录号和颜色是否一致。",
  "COLLECTED — CATALOG # AND COLOUR MATCH": "已取货 — 目录号和颜色一致",
  "Storage": "仓库",
  "Pick up from warehouse": "从仓库取货",
  "Pick up the part": "取出零件",
  "PICK UP THE PART": "取出零件",
  "TO PICK UP": "待取货",
  "PRODUCT FOUND": "已找到产品",
  "NOT FOUND": "未找到",
  "PRODUCTION NEEDED": "需要生产",
  "NO INVENTORY": "无库存",
  "VEHICLE MISMATCH": "车型不符",
  "SPEC ALERT": "规格不符",
  "On Hold": "暂停",
  "ON HOLD": "暂停中",
  "Remove hold": "解除暂停",
  "Put on hold": "设为暂停",
  "Hold removed": "已解除暂停",
  "Print storage label": "打印仓储标签",
  "Print shipping label": "打印运输标签",
  "US On": "美国 开",
  "US Off": "美国 关",
  "Pick from Canada storage": "从加拿大仓库提货",
  "Pick from China / USA warehouse": "从中国/美国仓库提货",
  "Production needed": "需要生产",
  "Confirm NOT FOUND → Production needed": "确认未找到 → 需要生产",
  "Product found — stock −1. Continue.": "已找到产品 — 库存 −1。请继续。",
  "Product found (no stock record to deduct). Continue.": "已找到产品（无库存记录可扣减）。请继续。",
  "Find the item in storage and check it matches the catalog number, colour and factory below.": "在仓库中找到该物品，并核对下方的目录编号、颜色和工厂。",
  "Factory codes": "工厂代码",
  "Bin / shelf": "货位 / 货架",
  "different colour": "颜色不同",
  "ordered": "订购",
  "approved by": "批准人",
  "white number": "白色数字",
  "black number": "黑色数字",
  "Sent by": "发送人",
  "Note (optional) — shelf / bin, or why it was not found": "备注（可选）— 货架 / 货位，或未找到的原因",
  "Not found sets this colour's count in the": "“未找到”会将该颜色在",
  "warehouse to 0 and sends the order back to Advance Orders with a PRODUCTION NEEDED alert.": "仓库的数量设为 0，并将订单退回预付订单并发出“需要生产”提醒。",
  "Waiting for": "等待",
  "to pick up and confirm the part.": "取货并确认零件。",
  "3PL Pick-up": "3PL 取货",
  "USA warehouse": "美国仓库",
  "warehouse": "仓库",
  "Pick the part by Catalog # + colour. Found → enter the tracking number and mark shipped. Not found → the order goes back to Advance Orders as PRODUCTION NEEDED.": "按目录编号 + 颜色取出零件。找到 → 输入追踪号并标记已发货。未找到 → 订单退回预付订单并标记“需要生产”。",
  "to pick up": "待取货",
  "to ship": "待发货",
  "Nothing waiting for pick-up.": "没有待取货的订单。",
  "Found — ship with tracking": "已找到 — 填追踪号发货",
  "No parts waiting to be shipped.": "没有待发货的零件。",
  "Shipped by 3PL": "3PL 已发货",
  "Nothing shipped yet.": "尚未发货。",
  "found by": "找到人",
  "Tracking number": "追踪号",
  "Carrier (optional)": "承运商（可选）",
  "Mark shipped": "标记已发货",
  "marked shipped": "已标记发货",
  "The order skips Production. Choose where the part is and which factory's cover to take — the station picks it up and confirms Product found / Not found.": "订单跳过生产。选择零件所在仓库及取用哪个工厂的车罩 — 站点取货后确认“已找到 / 未找到”。",
  "Inventory shows no stock for this choice — you can still send it; the count will not go below 0.": "库存显示此选择无库存 — 仍可发送；数量不会低于 0。",
  "Send to": "发送至",
  "Choose a warehouse + factory": "选择仓库 + 工厂",
  "CANADA WAREHOUSE": "加拿大仓库",
  "USA WAREHOUSE": "美国仓库",
  "China warehouse": "中国仓库",
  "in stock": "有库存",
  "The order skips Production. Packing Orders collects the item from the China warehouse, confirms it matches, then packs as usual.": "订单跳过生产。包装部从中国仓库取货，确认匹配后照常包装。",
  "China stock": "中国库存",
  "Waiting for Packing Orders to collect and confirm the item.": "等待打包站取货并确认。",
  "IN TRANSIT TO WAREHOUSE": "运往仓库中",
  "In transit to warehouse": "运往仓库中",
  "In transit": "运输中",
  "Shipped to client": "已发给客户",
  "COMPLETE PACKING": "包装完成",
  "Back to Pre Order": "退回预订单",
  "Moving…": "移动中…",
  "Could not move to Pre Order": "无法移至预订单",
  "Send this order back to the Pre Orders tab for a fresh review": "将此订单退回预订单页签重新审核",
  "Reason for sending this order back to Pre Order? (optional)": "退回预订单的原因？（可选）",
  "This lead may belong to an existing order": "此线索可能属于一个现有订单",
  "A lead may belong to this order": "有一条线索可能属于此订单",
  "VIN matches merge automatically — this one matched on contact details, please confirm": "VIN 匹配会自动合并 — 此条仅凭联系方式匹配，请确认",
  "Merge into": "合并到",
  "Merge lead into this order": "将线索合并到此订单",
  "Not the same car": "不是同一辆车",
  "Merging…": "合并中…",
  "Merged": "已合并",
  "Merge failed": "合并失败",
  "Suggestion hidden": "已隐藏建议",
  "same e-mail": "相同邮箱",
  "same phone": "相同电话",
  "same customer": "相同客户",
  "Production Orders": "生产订单",
  "Product Transfer": "产品转移",
  "Packing Orders": "包装订单",
  "Orders Received": "已收货订单",
  "Shipping Orders": "运输订单",
  "Order Fulfilled": "订单已完成",
  "Inventory": "库存",
  "Bulk Orders": "批量订单",
  "Billing": "账单",
  "Users": "用户",
  "Team": "团队",
  "Settings": "设置",
  "Production / Confirmed": "生产 / 已确认",
  "Production": "已确认",
  "Completed": "已完成",

  // -------------- Roles --------------
  "Super Admin": "超级管理员",
  "Back Office": "后台",
  "Factory 1": "工厂一",
  "Factory 2": "工厂二",
  "Factory 3": "工厂三",
  "Shipping Order": "运输订单员",
  "Administrator": "管理员",
  "Manager": "经理",
  "Employee": "员工",

  // -------------- Stage labels (singular UI pills) --------------
  "Lead": "线索",
  "PRE-ORDER": "预订单",
  "PENDING": "待处理报价",
  "ADVANCE": "预付",
  "IN PRODUCTION": "生产中",
  "IN TRANSFER": "转移中",
  "IN PACKING": "包装中",
  "INV PACKING": "库存包装",
  "SHIPPING": "运输",
  "RECEIVED": "已收货",
  "FULFILLED": "已完成",
  "NA": "无此型号",
  "FLAGGED": "已标记",
  "ABANDONED": "已放弃",
  "CANCELLED": "已取消",
  "NO MOLD": "无模具",
  "LEAD": "线索",

  // -------------- Page section headings --------------
  "Orders": "订单",
  "Sort": "排序",
  "Default": "默认",
  "Global search": "全局搜索",
  "Alert feed": "警报动态",
  "To Be Received": "待收货",
  "Received": "已收货",
  "Cancelled / Refunded": "已取消 / 已退款",
  "Also found in other tabs": "在其他标签页中也找到",
  "Master Catalog · Vehicle → Product Database": "主目录 · 车辆 → 产品数据库",
  "Inventory Management": "库存管理",
  "Suggest the right caliper cover for any vehicle. Track stock, reserved, reorder points and bin locations.":
    "为任何车辆推荐合适的卡钳罩。跟踪库存、预留、补货点和货位。",
  "Google Drive": "Google 云端硬盘",
  "Shopify Webhook": "Shopify Webhook",
  "Preview mode — customer emails suppressed": "预览模式 — 客户邮件已被屏蔽",
  "Maintenance": "维护",
  "Photo Reminder Emails": "照片提醒邮件",
  "Danger Zone": "危险操作区",
  "🔐 User Password Vault": "🔐 用户密码保管库",
  "📋 Packing Checklist Template": "📋 包装清单模板",
  "Sign Off — Production Complete": "签字确认 — 生产完成",
  "Sign & Start Production": "签字并开始生产",
  "back to list": "返回列表",
  "Order Info": "订单信息",
  "Photos": "照片",
  "Notes": "备注",
  "📧 Emails": "📧 邮件",
  "Change History": "变更历史",
  "Packing Checklist": "包装清单",
  "Generated from inventory take-outs.": "由库存出库生成。",

  // -------------- Packing wizard (checklist steps) --------------
  "Step": "步骤",
  "of": "/",
  "completed": "完成",
  "Note": "备注",
  "PO / Catalog": "订单号 / 目录号",
  "Color": "颜色",
  "Front Cover Code": "前轮代码",
  "Front Clamp Type": "前卡钳类型",
  "Rear Cover Code": "后轮代码",
  "Rear Clamp Type": "后卡钳类型",
  "Quality Check": "质量检查",
  "Leprecon+ Label": "Leprecon+ 标签",
  "Clamp Label": "卡钳标签",
  "Logo Label": "标志标签",
  "Picture to Drive": "上传照片至 Drive",
  "Spacer Type": "垫片类型",
  "Tool Kit": "工具包",
  "Gift": "礼品",
  // Wizard prompts
  "Confirm the PO number and catalog on the label matches the cover.": "确认标签上的订单号和目录号与罩体匹配。",
  "Is the cover the correct color?": "罩体颜色是否正确？",
  "Read the front cover tag — does it match?": "读取前轮标签 — 是否匹配？",
  "Verify the front clamp type matches.": "确认前卡钳类型匹配。",
  "Read the rear cover tag — does it match?": "读取后轮标签 — 是否匹配？",
  "Verify the rear clamp type matches.": "确认后卡钳类型匹配。",
  "Inspect Paint finish & edges. Any defects?": "检查油漆表面和边缘。是否有瑕疵？",
  "Is the Leprecon+ label affixed and legible?": "Leprecon+ 标签是否粘贴牢固且清晰可读？",
  "Is the Clamp Label Type present on both Front and Rear covers?": "前轮和后轮是否都贴有配件标签类型？",
  "Match the customer logo label — is it correct?": "对照客户标志标签 — 是否正确？",
  "Take a photo of the packed cover and upload it to Drive.": "为已打包的罩体拍照并上传至 Drive。",
  "Include the correct spacer type and quantity per this order.": "按订单要求放入正确的垫片类型和数量。",
  "Include the tool kit (Allen keys, wrench).": "放入工具包（内六角扳手、扳手）。",
  "Include the branded gift in the box.": "在包装盒中放入品牌礼品。",
  // Wizard buttons + messages
  "✓ Confirm": "✓ 确认",
  "✓ Confirmed — Next": "✓ 已确认 — 下一步",
  "🛑 Report Issue": "🛑 报告问题",
  "Back": "返回",
  "Skip to sign-off": "跳至签字",
  "Take at least one photo before confirming": "确认前请至少拍摄一张照片",
  "A photo is required before you can confirm this step.": "确认此步骤前必须拍摄一张照片。",
  "Take Photo": "拍照",
  "Uploading…": "上传中…",
  "uploaded": "已上传",
  "All": "全部",
  "checks completed": "项检查已完成",
  "Employee Signature": "员工签名",
  "Signed as": "签名为",
  "Sign your name, then create the ShipStation label to close out this station.": "签上您的名字，然后创建 ShipStation 标签以结束此工位。",
  "Mark the order as Ready to be Shipped — you can create the label and ship later from the Inv Packing list.": "将订单标记为待发货 — 您可以稍后从库存包装列表创建标签并发货。",
  "✅ Mark Ready to be Shipped": "✅ 标记为待发货",
  "🚚 Create ShipStation Label": "🚚 创建 ShipStation 标签",
  "🛍 Ship from Shopify": "🛍 从 Shopify 发货",
  "✓ Self Checkout": "✓ 自助结账",
  "US destination — USMCA required": "美国目的地 — 需要 USMCA",
  "Show USA flag": "显示美国标志",
  "Hide USA flag": "隐藏美国标志",
  "— brand —": "— 品牌 —",
  "Brand code": "品牌代码",
  "Model #": "型号",
  // User glossary (Sep 19, 2026)
  "Pending Order": "待处理报价",
  "Submit Codes → Advance": "提交报价",
  "Cover codes need a brand code + number": "轮罩代码需要品牌代码 + 编号",
  "Not Available → NA": "无此型号",
  "Not Available": "无此型号",
  "not available": "无此型号",
  "BAD PHOTOS": "照片不清",
  "awaiting payment": "等待付款",
  "Make a claim": "提出索赔",
  "Claim": "索赔",
  "Claims": "索赔",
  "After-sale QC": "售后质检",
  "Defective": "有缺陷",
  "Incompatible": "不兼容",
  "Missing": "缺件",
  "OPEN": "待处理",
  "OPEN · AFTER-SALE QC": "待处理 · 售后质检",
  "Warranty replacement": "保修更换",
  "Paid re-order": "付费重订",
  "Decline": "拒绝",
  "Declined": "已拒绝",
  "Warranty": "保修",
  "Re-order": "重订",
  "Full set": "整套",
  "Front cover only": "仅前盖",
  "Rear cover only": "仅后盖",
  "Clamps only": "仅卡扣",
  "What should Factory One make?": "一厂需要生产什么？",
  "What happened?": "发生了什么问题？",
  "File claim": "提交索赔",
  "Open": "待处理",
  "Decided": "已决定",
  "All": "全部",
  "No claims here.": "暂无索赔。",
  "filed by": "提交人",
  "decided by": "决定人",
  "Decision note": "决定备注",
  "fulfilled": "已发货",
  "Decide each claim: warranty replacement, paid re-order or decline. Back Office is alerted with your decision.": "处理每个索赔：保修更换、付费重订或拒绝。后台会收到您的决定提醒。",
  "Claims you filed on fulfilled orders — Factory One / Super Admin decide.": "您对已发货订单提出的索赔 — 由一厂 / 超级管理员决定。",
  "Read-only — Factory One and Super Admin decide.": "只读 — 由一厂和超级管理员决定。",
  "Bad photos": "照片不清",
  "Bad photos — send back to Pre Orders": "照片不清 — 退回预订单",
  "What is wrong with the photos?": "照片有什么问题？",
  "Your note is translated automatically (English ⇄ 中文) for the Back Office.": "备注会自动翻译（英文 ⇄ 中文）给后台。",
  "Send back — bad photos": "退回 — 照片不清",
  "Translating…": "翻译中…",
  "The order returns to Pre Orders with a 📷 BAD PHOTOS mark and Back Office gets an alert to ask the customer for new photos.": "订单将退回预订单并标记 📷 照片不清，后台会收到提醒并联系客户重新拍照。",
  "e.g. rear caliper photo is blurry, cannot read the brand": "例如：后卡钳照片模糊，看不清品牌",
  "Front Cover": "前轮",
  "Rear Cover": "后轮",
  "F-Clamp": "前轮配件",
  "R-Clamp": "后轮配件",
  "F-Clamp Code": "前轮配件代码",
  "R-Clamp Code": "后轮配件代码",
  // Packing QC hold flow
  "ON HOLD · QC ISSUE": "暂停 · 质检问题",
  "QUALITY CONTROL": "质量控制",
  "Stop & Report Issue": "停止并报告问题",
  "Photos (optional, up to 5)": "照片（可选，最多 5 张）",
  "📷 Add photo": "📷 添加照片",
  "Remove": "移除",
  "🛑 Submit Issue Report": "🛑 提交问题报告",
  "Issue reported — Shipping Desk, Back Office & Super Admin notified": "问题已报告 — 已通知发货台、后台和超级管理员",
  "Accepted": "已接受",
  "Send Back": "退回",
  "Confirm Accepted": "确认接受",
  "Confirm Send Back": "确认退回",
  "Note (optional)": "备注（可选）",
  "Shown to the packing station and the factory": "显示给包装站和工厂",
  "The packing station will be told to continue packing this order.": "将通知包装站继续包装此订单。",
  "The order moves to Production · QUALITY CONTROL. The factory receives the issue note and photos; the packing station is told to send the item back.": "订单将转入生产 · 质量控制。工厂将收到问题备注和照片；包装站将被通知退回产品。",
  "Accepted — packing station told to continue": "已接受 — 已通知包装站继续",
  "Sent back to Factory — Quality Control": "已退回工厂 — 质量控制",
  "QC Resolved": "质检已解决",
  "Quality Control resolved": "质量控制问题已解决",
  "Packing issue": "包装问题",
  "Quality Control issue": "质量控制问题",
  "Reported by": "报告人",
  "Response": "回复",
  "Waiting for Shipping Desk / Back Office / Super Admin to respond.": "等待发货台 / 后台 / 超级管理员回复。",
  "Order is ON HOLD — packing is locked until the issue is answered.": "订单已暂停 — 在问题得到回复前包装已锁定。",
  "Resolve the Quality Control issue before completing production.": "完成生产前请先解决质量控制问题。",
  "Sent back by": "退回人",
  "ON HOLD": "暂停",
  "View": "查看",
  "Accept": "接受",
  "Follow": "跟进",
  "Following": "跟进中",
  "Following · reminder in ≤2h": "跟进中 · 2 小时内提醒",
  "target": "对象",
  "Alerts targeted to your role.": "针对您角色的提醒。",
  "Global alerts for all users.": "所有用户的全局提醒。",
  "No alerts. All stations on time.": "没有提醒。所有工位均按时进行。",
  "open alerts": "个未处理",
  "Marked received → moved to Inventory Packing": "已标记收货 → 已移至库存包装",
  "CONVERTED → PO": "已转换 → PO",
  "Waiting Reply": "等待回复",
  // Alert headline templates (see i18n/alerts.js)
  "Pre-Order {po} — photos received, not approved in 2 days": "预订单 {po} — 已收到照片，2 天内未批准",
  "Factory codes missing on {po} for 2 days": "{po} 的工厂代码已缺失 2 天",
  "{po} in production > 7 days — please provide new ETA": "{po} 生产超过 7 天 — 请提供新的预计完成日期",
  "{po} not finished within 7 days — deadline {eta}": "{po} 未在 7 天内完成生产 — 截止 {eta}",
  "{po} in production not signed for 48h": "{po} 进入生产已超过 48 小时未签字",
  "{po} signed but not printed for 48h": "{po} 已签字但超过 48 小时未打印",
  "⏰ {po} requests {days} more day(s)": "⏰ {po} 申请延期 {days} 天",
  "✓ {po} extension approved (+{days} days) — proceed": "✓ {po} 延期已批准（+{days} 天）— 请继续",
  "Not signed 48h": "48 小时未签字",
  "Not printed 48h": "48 小时未打印",
  "Extension request": "延期申请",
  "Extension approved": "延期已批准",
  // Factory One production list actions
  "To Print": "待打印",
  "Print": "打印",
  "Print selected": "打印所选",
  "Print all To-Print": "打印全部待打印",
  "Sign selected": "签字所选",
  "Daily orders": "日常订单",
  "Batch orders": "批量订单（备货）",
  "Batch · Warehouse": "批次 · 仓库",
  "no codes in catalog yet": "目录中尚无代码",
  "Return": "返回",
  "Sign Off & Transfer": "签字完成并转移",
  "Need More Time": "需要更多时间",
  "Extension requested": "已申请延期",
  "Extra days": "额外天数",
  "Request more time": "申请更多时间",
  "Request sent — awaiting approval": "申请已发送 — 等待批准",
  "Why is more time needed?": "为什么需要更多时间？",
  "Super Admin and the Factory One Administrator will be asked to approve. Once approved, the 7-day deadline is extended by these days and HQ2 is notified.": "将请求超级管理员和工厂一管理员批准。批准后，7 天截止日期将延长相应天数，并通知 HQ2。",
  "Approve": "批准",
  "Reject": "拒绝",
  "Approved": "已批准",
  "Rejected": "已拒绝",
  "Deadline": "截止日期",
  "Due": "截止",
  "days": "天",
  "For Production": "生产用",
  "Printed on": "打印日期",
  "Printed by": "打印人",
  "Clamps": "夹具",
  "Signature": "签名",
  "PO #": "订单号",
  "Sending…": "发送中…",
  "Signed — now print the production sheet": "已签字 — 请打印生产单",
  "Production sheet printed": "生产单已打印",
  "Reason for sending this order back? (optional)": "退回此订单的原因？（可选）",
  // Shipping boxes
  "Make Box": "打包成箱",
  "BOX": "箱",
  "order(s)": "个订单",
  "One tracking # for the whole box": "整箱一个追踪号",
  "Ship Box": "发运整箱",
  "Unbox": "取消装箱",
  "Remove these orders from the box": "将这些订单从箱中移除",
  "uses the box tracking #": "使用整箱追踪号",
  "in box": "已装箱",
  "Box cleared": "已取消装箱",
  "shipped with": "已发运，追踪号",
  "{po} delayed — new ETA {eta}": "{po} 延期 — 新预计日期 {eta}",
  "{po} missed new ETA {eta} — still in production": "{po} 已错过预计日期 {eta} — 仍在生产中",
  "No tracking for {po} in 7 days": "{po} 7 天内无追踪号",
  "Order {po} flagged": "订单 {po} 已标记",
  "Order {po} moved to NA": "订单 {po} 已移至 NA",
  "🔔 Reminder · {po} · {detail}": "🔔 提醒 · {po} · {detail}",
  "Batch {date} unpaid for {days} days · ¥{amount}": "批次 {date} 已 {days} 天未付款 · ¥{amount}",
  "Batch {date} paid — ¥{amount}": "批次 {date} 已付款 — ¥{amount}",
  "🛑 Packing issue on {po}": "🛑 {po} 包装问题",
  "✓ Issue accepted on {po} — continue packing": "✓ {po} 的问题已接受 — 继续包装",
  "🔴 Quality Control · {po}": "🔴 质量控制 · {po}",
  "↩ Send {po} back to Factory — Quality Control": "↩ 将 {po} 退回工厂 — 质量控制",
  "Pre-approval overdue": "预批准逾期",
  "Factory codes overdue": "工厂代码逾期",
  "Production overdue": "生产逾期",
  "Production delay notice": "生产延期通知",
  "Production ETA missed": "错过生产预计日期",
  "Tracking missing": "缺少追踪号",
  "Flagged order": "已标记订单",
  "NA order": "NA 订单",
  "Order reminder": "订单提醒",
  "Batch unpaid overdue": "批次付款逾期",
  "Batch paid": "批次已付款",
  "Packing hold": "包装暂停",
  "Packing hold · accepted": "包装暂停 · 已接受",
  "Continue": "继续",
  "Remove this order from the alerts": "从提醒中移除此订单",
  "QC Resolved": "质检已解决",
  "by": "由",
  "Hold accepted": "暂停已接受",
  "Quality control": "质量控制",
  "QC send back": "质检退回",
  "critical": "紧急",
  "warning": "注意",
  "info": "信息",
  "A USMCA Certificate of Origin (3 copies) will open automatically when you tap any of the shipping actions below. You can also open it now:": "点击下方任意发货操作时，USMCA 原产地证书（3 份）将自动打开。您也可以立即打开：",
  "📄 Open USMCA (3 copies)": "📄 打开 USMCA（3 份）",
  "Any of these three actions closes this station and moves the order to": "以上任一操作将关闭此工位并将订单移至",
  "Order Fulfilled": "订单已完成",
  "Review all": "查看全部",
  "items": "项",
  "Add a packing note": "添加打包备注",
  "Shared with Back Office and every other tab under this order's": "与后台以及此订单的其他所有标签页共享 —",
  "section.": "部分。",
  "Previous packing notes": "以往的打包备注",
  "Save note": "保存备注",
  "Marking...": "标记中...",
  "✓ Mark Received": "✓ 标记为已收货",
  "as": "作为",
  "START PACKING": "开始打包",
  "Shipping…": "发货中…",
  "🚚 SHIP": "🚚 发货",
  "status only": "仅状态",
  "Reactivating…": "重新激活中…",
  "🔄 Reactivate": "🔄 重新激活",
  // Compact packing checklist grid pill labels
  "PO CAT": "订单 / 目录号",
  "COLOR": "颜色",
  "FRONT COVER": "前轮",
  "F CLAMP": "前卡钳",
  "REAR COVER": "后轮",
  "R CLAMP": "后卡钳",
  "QUALITY": "质量",
  "LP LABEL": "LP 标签",
  "CLAMP LABEL": "卡钳标签",
  "LOGO LABEL": "标志标签",
  "PICTURE TO DRIVE": "上传照片",
  "SPACERS": "垫片",
  "TOOL KIT": "工具包",
  "GIFT": "礼品",
  "Tap each item as you complete it. All items required before signing off.": "完成每一项后请点击。签字前必须完成所有项目。",
  "items checked": "项已完成",
  "complete all items to enable Finish": "完成所有项目后方可结束",
  "✓ Finish & Send to Shipping": "✓ 完成并发往运输",
  "🛑 Stop / Report Issue": "🛑 停止 / 报告问题",
  // Header action buttons
  "Cancel Order": "取消订单",
  "Send Email": "发送邮件",
  "Rush": "加急",
  "MANUAL UPLOAD": "手动上传",
  "PHOTOS RECEIVED": "已收到照片",
  "Order is packed and ready for shipping.": "订单已打包完毕，准备发货。",
  "✅ Send to Shipping": "✅ 发送到运输",
  "✅ READY TO SHIP": "✅ 待发货",
  // Order-row stage pills (in-row status labels)
  "Complete Packing": "包装完成",
  "Packing Ready": "待打包",
  "Order Received": "订单已收货",
  "Complete Sign-off": "签字完成",
  "In Production": "生产中",
  "Awaiting Signature": "等待签字",
  "CHECK COMPLETED": "核对完成",
  "PENDING CHECK": "待核对",
  "PENDING ORDERS": "待处理报价",
  "CODES RECEIVED": "已收到代码",
  "READY TO BE SHIPPED": "待发货",
  "NOT READY": "未就绪",
  // Single-side (FF/RR) order rule
  "Single-side order": "单侧订单",
  "no front": "无前轮",
  "no rear": "无后轮",
  "front pair not ordered": "未订购前轮",
  "rear pair not ordered": "未订购后轮",
  // Ready to Ship tab
  "Ready to Ship": "待发货",
  "packed and waiting for a shipping label": "已打包，等待运单",
  "USA Delivery": "美国配送",
  "🇨🇦 Ship to Canada / Rest of World": "🇨🇦 发往加拿大 / 其他地区",
  "No USA orders waiting to ship.": "没有等待发往美国的订单。",
  "No Canada / rest-of-world orders waiting to ship.": "没有等待发往加拿大或其他地区的订单。",
  "Packed On": "打包日期",
  "Packed By": "打包人",
  "Destination": "目的地",

  // -------------- Common list / batch section --------------
  "📦 Packing Ready": "📦 待包装",
  "📦 INV Packing Ready": "📦 待库存包装",
  "✓ Complete Packing": "✓ 完成包装",
  "✓ Complete INV Packing": "✓ 完成库存包装",
  "Waiting for Arrival": "等待到货",
  "Select all": "全选",
  "Clear selection": "清除选择",
  "HQ Order": "总部订单",
  "HQ Check": "总部核对",

  // -------------- Table column headers --------------
  "Customer": "客户",
  "Factory": "工厂",
  "Vehicle": "车辆",
  "Color": "颜色",
  "Colour": "颜色",
  "CAT #": "目录号",
  "Codes": "代码",
  "Logo": "标志",
  "Stage": "阶段",
  "Status": "状态",
  "Brand": "品牌",
  "Model": "型号",
  "Year": "年份",
  "VIN": "车架号",
  "Rim Size": "轮辋尺寸",
  "Tracking": "运单号",
  "Tracking #": "运单号",
  "Shipped": "已发货",
  "Shipped On": "发货日期",
  "Warehouse": "仓库",
  "Remove from selection": "从选择中移除",
  "Ticked orders stay selected while you search for more.": "已勾选的订单在继续搜索时保持选中。",
  "New box": "新箱",
  "Box #": "箱号",
  "already exists today — the orders will be added to it.": "今天已存在——订单将加入该箱。",
  "Add to an existing box": "加入现有箱",
  "Add to box": "加入箱",
  "Box note": "箱备注",
  "visible in Orders Received": "在已收货订单中可见",
  "e.g. fragile · 2 spacers packs inside · ship with BOX 3": "例如：易碎 · 内含2包垫片 · 与3号箱一起发",
  "📝 Box note (visible in Orders Received)": "📝 箱备注（在已收货订单中可见）",
  "Save note": "保存备注",
  "Box note saved": "箱备注已保存",
  "Remove this order from the box": "将此订单移出箱子",
  "Send back": "退回",
  "Whole box": "整箱",
  "Tick / untick every order in this box": "勾选/取消勾选此箱内所有订单",
  "Envelope": "信封",
  "Confirm logo": "确认标志",
  "Logo confirmed": "标志已确认",
  "Confirm the logo to unlock the envelope label": "确认标志后才能打印信封标签",
  "Marked received · ✉ USA order stays here — confirm logo, then print the envelope label": "已标记收货 · ✉ 美国订单留在此处——确认标志后打印信封标签",
  "This USA order is in Inventory Packing": "此美国订单在库存包装中",
  "Keep here for envelope": "留在此处做信封",
  "kept in Orders Received": "已留在已收货订单",
  "READY FOR PICK UP": "等待自取",
  "🏬 PICK UP READY": "🏬 可自取",
  "Ready for pick up": "等待自取",
  "email sent to": "邮件已发送至",
  "email sent": "邮件已发送",
  "Sending…": "发送中…",
  "🏬 Pick up: on": "🏬 自取：开",
  "Pick up: off": "自取：关",
  "Recheck codes": "重新核对代码",
  "Send back to Production": "退回生产",
  "sent back to Production": "已退回生产",
  "Sent back from Product Transfer": "从产品移交退回",
  "Return this order to the factory (Production)": "将此订单退回工厂（生产）",
  "The order leaves Product Transfer and returns to the factory's Production list. The factory sign-off is cleared so they must sign off and transfer it again.": "订单离开产品移交并返回工厂的生产列表。工厂签收将被清除，需要重新签收并移交。",
  "Reason (optional) — e.g. item never arrived at packing, wrong cover in the bag…": "原因（可选）——例如：货未到包装站、袋内罩子有误……",
  "optional — ships the box right away": "可选——填写后立即发货",
  "ETA is set automatically to": "预计到达自动设为发货后",
  "days after shipping — change it if needed.": "天——如有需要可修改。",
  "Ship": "发货",
  "ETA": "预计到达",
  "Arrival": "到达",
  "Date Arrived": "到达日期",
  "BIN #": "货位号",
  "Received By": "收货人",
  "Reason": "原因",
  "Cancelled On": "取消日期",
  "Completed On": "完成日期",
  "Created At": "创建时间",
  "Updated At": "更新时间",
  "Rush": "加急",
  "Action": "操作",
  "Actions": "操作",
  "Name": "姓名",
  "Role": "角色",
  "Created": "创建时间",
  "Contact": "联系方式",
  "Tier": "级别",
  "Station": "工位",
  "Access": "权限",
  "Email": "邮箱",
  "Phone": "电话",
  "Pending Shipping": "待发货",

  // -------------- Sort dropdown options --------------
  "Order # · Oldest first": "订单号 · 最旧优先",
  "Order # · Newest first": "订单号 · 最新优先",
  "Stage · A → Z (lead first)": "阶段 · A → Z（线索优先）",
  "Stage · Z → A (cancelled first)": "阶段 · Z → A（已取消优先）",
  "Photos Received · Oldest first": "已收照片 · 最旧优先",
  "Photos Received · Newest first": "已收照片 · 最新优先",
  "Waiting Reply · Oldest first": "等待回复 · 最旧优先",

  // -------------- Buttons --------------
  "Save": "保存",
  "Save & Ship": "保存并发货",
  "Cancel": "取消",
  "Delete": "删除",
  "Edit": "编辑",
  "Create": "创建",
  "Update": "更新",
  "Confirm": "确认",
  "Close": "关闭",
  "Open": "打开",
  "Submit": "提交",
  "Search": "搜索",
  "Filter": "筛选",
  "Refresh": "刷新",
  "Loading…": "加载中…",
  "Searching…": "搜索中…",
  "Syncing…": "同步中…",
  "Saving…": "保存中…",
  "Adding tab…": "添加标签页…",
  "No orders in this stage.": "此阶段没有订单。",
  "No pending shipments — everything shipped is now in Orders Received.":
    "没有待发货订单 — 所有已发货订单现在都在已收货订单中。",
  "No orders received yet.": "尚未收到任何订单。",
  "Queue clear — no orders waiting to be packed.": "队列已清空 — 没有等待包装的订单。",
  "No packed history yet.": "尚无包装记录。",
  "Nothing waiting to be received.": "没有等待接收的订单。",
  "Log out": "退出登录",
  "Report": "报表",
  "back to report": "返回报表",
  "WAITING FOR REPLY": "等待客户回复",
  "Pick up → Fulfilled": "自提 → 已完成",
  "Pick up instead of shipping": "改为自提（不发货）",
  "will be flagged as a Pick Up order and moved to Order Fulfilled as READY FOR PICK UP. No shipping label is needed and no email will be sent to the client.": "将被标记为自提订单并移至“订单已完成 · 等待自提”。无需运输标签，也不会向客户发送邮件。",
  "Confirm pick up": "确认自提",
  "Fold": "折叠",
  "Dark": "深色",
  "Light": "浅色",
  "Switch side menu to dark": "侧边栏切换为深色",
  "Switch side menu to light": "侧边栏切换为浅色",
  "Unfold": "展开",
  "Fold menus": "折叠子菜单",
  "Unfold menus": "展开子菜单",
  "Re-order": "重新下单",
  "Request Re-order": "申请重新下单",
  "Re-order requested": "已申请重新下单",
  "waiting for Factory One administrator": "等待一厂管理员审批",
  "Re-order rejected": "重新下单被拒绝",
  "Re-order requested — waiting for Factory One administrator approval": "已申请重新下单 — 等待一厂管理员审批",
  "Colour actually received": "实际收到的颜色",
  "Select colour…": "选择颜色…",
  "colour received": "收到的颜色",
  "Approve re-order": "批准重新下单",
  "Reject re-order": "拒绝重新下单",
  "Reject": "拒绝",
  "Confirm approval": "确认批准",
  "Confirm rejection": "确认拒绝",
  "Re-order approved": "重新下单已批准",
  "Re-order rejected — hold stays open": "重新下单被拒绝 — 暂停仍然有效",
  "A new order PO-XXXX-R is created in Production and billed again. The wrong item goes to Inventory (CHINA warehouse) and the packing station is told to store it.": "将创建新订单 PO-XXXX-R 进入生产并再次计费。错误的产品转入库存（中国仓库），并通知包装工位存放。",
  "The hold stays open — Shipping Desk / Back Office can still Accept or Send Back.": "暂停仍然有效 — 发货台/后台仍可选择接受或退回。",
  "A new order PO-XXXX-R goes to Production and is billed again. The wrong item is transferred to Inventory (CHINA warehouse, same catalog #) and the packing station is told to store it. Needs the Factory One administrator's approval.": "新订单 PO-XXXX-R 进入生产并再次计费。错误的产品转入库存（中国仓库，相同目录号），并通知包装工位存放。需要一厂管理员审批。",
  "The order moves to Production · QUALITY CONTROL as PO-XXXX-D1. The factory receives the issue note and photos; the packing station is told to send the item back.": "订单以 PO-XXXX-D1 进入生产 · 质量控制。工厂收到问题备注和照片；通知包装工位退回产品。",
  "Stored": "已存放",
  "Stored — thank you": "已存放 — 谢谢",
  "Re-ordered as": "已重新下单为",
  "Re-ordered": "已重新下单",
  "Re-order of": "重新下单来源",
  "— select —": "— 请选择 —",
  "WARRANTY": "保修",
  "REORDER": "重新下单",
  "Reorder": "重新下单",
  "Marked as Reorder by": "标记为重新下单：",
  "Marked as Reorder — pinned to the top in light red": "已标记为重新下单 — 置顶并显示为浅红色",
  "Reorder mark removed": "已移除重新下单标记",
  "Remove the Reorder mark": "移除重新下单标记",
  "Mark as Reorder — top of every production tab, light red row": "标记为重新下单 — 置顶于每个生产标签页，浅红色行",
  "Only Back Office sends new logos": "只有后台可以寄送新标志",
  "Decide each claim: warranty replacement, paid re-order, refund or declined/resolved. Your solution note is shown to Back Office.": "处理每个索赔：保修更换、付费重新下单、退款或拒绝/已解决。您的解决方案备注将显示给后台。",
  "received": "收到",
  "Item transferred to Inventory": "产品已转入库存",
  "Catalog #": "目录号",
  "colour": "颜色",
  "waiting for the packing station to store it": "等待包装工位存放",
  "🔁 {po} re-order approval needed — {reason}": "🔁 {po} 需要审批重新下单 — {reason}",
  "📦 {po} — store in {warehouse} warehouse under Catalog # {cat} · {reason}": "📦 {po} — 存放到{warehouse}仓库，目录号 {cat} · {reason}",
  "🧩 {po} — put the set in SPARE PARTS ({warehouse}) · Catalog # {cat} · {reason}": "🧩 {po} — 整套放入备件区（{warehouse}） · 目录号 {cat} · {reason}",
  "Spare parts": "备件",
  "No own box": "没有自己的包装箱",
  "Front clamps only": "仅前卡夹",
  "Rear clamps only": "仅后卡夹",
  "Front + rear clamps": "前 + 后卡夹",
  "CLAMP COUNT": "卡夹数量",
  "SHIPS INSIDE BOX OF": "随以下订单的箱子发运",
  "Clamp count": "卡夹数量",
  "Ships inside box of": "随以下订单的箱子发运",
  "INSIDE": "装在",
  "SEPARATED FROM": "已从以下箱子取出",
  "CARRIES": "附带",
  "Carries": "附带",
  "Inside box of": "装在以下订单的箱子里",
  "Separated from": "已取出，原箱",
  "inside": "装在",
  "Finish packing": "完成包装",
  "Which clamps?": "哪些卡夹？",
  "Front clamps": "前卡夹",
  "Rear clamps": "后卡夹",
  "Front + rear": "前 + 后",
  "Extra item in this box": "此箱有额外物品",
  "for warranty": "保修单",
  "Take them out before this box goes to the customer": "在此箱发给客户前请取出",
  "and ship them separately.": "并单独发运。",
  "Clamps removed & handed to shipping": "卡夹已取出并交给发货",
  "take out before shipping to the customer": "发给客户前取出",
  "type a PO in Packing today…": "输入今天在包装中的 PO…",
  "Orders in Packing · same destination first": "包装中的订单 · 相同目的地优先",
  "same covers as": "护罩代码相同于",
  "In spare parts": "已放入备件",
  "In spare parts — thank you": "已放入备件 — 谢谢",
  "Why is it re-ordered?": "重新下单的原因？",
  "Select reason…": "选择原因…",
  "Type the reason": "输入原因",
  "Wrong colour received": "收到错误颜色",
  "No inventory": "无库存",
  "Defective set": "套件有缺陷",
  "Missing parts": "缺少零件",
  "Damaged in transit": "运输中损坏",
  "Other": "其他",
  "The set is stored in the CHINA warehouse under the same Catalog #.": "整套存放到中国仓库，使用相同目录号。",
  "The set goes to SPARE PARTS — not back into sellable stock.": "整套放入备件区 — 不计入可售库存。",
  "Set goes to SPARE PARTS": "整套放入备件区",
  "waiting for the packing station to put it in spare parts": "等待包装工位放入备件区",
  "Re-order approval": "重新下单审批",
  "Store in warehouse": "存放到仓库",
  "Restart production": "重新开始生产",
  "production restarted": "生产已重新开始",
  "Could not restart production": "无法重新开始生产",
  "Reason for restarting production? (optional)": "重新开始生产的原因？（可选）",
  "Clear the factory signature / print / sign-off so Factory One can start production again": "清除工厂签名/打印/完工签字，以便一厂重新开始生产",
  "Move straight to the packing station — order will be packed": "直接移至包装工位 — 订单将被包装",
  "Switch user": "切换用户",
  "resumed at": "已恢复至",
  "Could not resume order": "无法恢复订单",
  "Send back to the station it came from, keeping its progress": "退回到原工位，保留进度",
  "The order goes back to": "订单将退回到",
  "with all its progress kept": "并保留全部进度",
  "will get an alert with your note": "将收到带有您备注的提醒",
  "Flag reason": "标记原因",
  "Note for the station": "给工位的备注",
  "e.g. Codes verified — continue as is": "例如：代码已核对 — 按原样继续",
  "Sending…": "发送中…",
  "↩ {po} resumed from Flagged — continue at {stage}": "↩ {po} 已从标记订单恢复 — 请在{stage}继续",
  "Resumed from Flagged": "从标记订单恢复",
  "New Pre Order (manual)": "新建预订单（手动）",
  "+ Manual Lead": "+ 手动线索",
  "+ New Lead": "+ 新建线索",
  "Manual Pre Order": "手动预订单",
  "✓ Receive Selected": "✓ 接收选中",
  "🔄 Sync Codes from Drive": "🔄 从 Drive 同步代码",
  "🖨️ Reprint": "🖨️ 重打",
  "🚚 ShipStation": "🚚 ShipStation",
  "🛍 Shopify": "🛍 Shopify",
  "✓ Self Checkout": "✓ 自助结账",
  "📦 INV PACKING": "📦 库存包装",
  "🏭 Create F1 HQ Order": "🏭 创建 F1 总部订单",
  "🏭 Create F2 AOOA Order": "🏭 创建 F2 AOOA 订单",
  "📄 Create F1 HQ Check": "📄 创建 F1 总部核对",
  "📄 Create F2 AOOA Check": "📄 创建 F2 AOOA 核对",

  // -------------- Small labels / meta --------------
  "order(s)": "个订单",
  "match": "匹配",
  "matches": "匹配",
  "batch(es)": "批次",
  "result": "结果",
  "results": "结果",
  "selected": "已选",
  "available": "可用",
  "navigate": "导航",
  "open": "打开",
  "close": "关闭",
  "CRM Inventory": "CRM 库存",
  "ordered by Advance approval date": "按预付批准日期排序",

  // -------------- Alert strip severity --------------
  "CRITICAL": "严重",
  "ATTENTION": "注意",
  "INFO": "信息",

  // -------------- Rush queue --------------
  "Rush queue": "加急队列",
  "Every rushed order across all stages — Urgent first, then Top, then Highlight.": "所有阶段的全部加急订单 — 先紧急，再置顶，再高亮。",
  "rushed orders": "个加急订单",
  "All levels": "全部等级",
  "URGENT": "紧急",
  "TOP": "置顶",
  "HIGHLIGHT": "高亮",
  "All stages": "全部阶段",
  "Level": "等级",
  "Stage": "阶段",
  "Vehicle": "车辆",
  "Colour": "颜色",
  "Codes": "代码",
  "Factory": "工厂",
  "Rush set": "加急设置",
  "Deadline": "截止日期",
  "No rushed orders right now.": "当前没有加急订单。",
  "No rushed orders match this filter.": "没有符合此筛选的加急订单。",
  "Open stage list": "打开阶段列表",
  "overdue": "逾期",
  "days left": "天剩余",
  "due today": "今天到期",
  // -------------- Font-size toggle titles --------------
  "Small": "小",
  "Medium (default)": "中（默认）",
  "Large": "大",
  "Extra Large": "特大",
  // -------------- 🛑 Cancelled-order STOP (warehouse storage) --------------
  "STOP": "停止",
  "is cancelled — this order will stay in the": "已取消 — 此订单将留在",
  "AMERICA WAREHOUSE": "美国仓库",
  "CANADIAN WAREHOUSE": "加拿大仓库",
  "Do NOT ship it. Write the Catalog number and the full colour name": "请勿发货。请写上目录编号和完整颜色名称",
  "on the box sticker": "在箱子标签上",
  "on the box": "在箱子上",
  "put the box on the": "将箱子放到",
  "shelf, then confirm below.": "货架上，然后在下方确认。",
  "Catalog number": "目录编号",
  "written": "已写上",
  "Done — stored in": "完成 — 已存入",
  "Later": "稍后",
  "Nothing to store on": "无需入库：",
  "Cancelled by": "取消人",
  "This set is in the": "此套件位于",
  "confirms it, not your station.": "负责确认，而不是您的工位。",
  "Packing Orders (China)": "包装订单（中国）",
  "Inventory Packing (Canada)": "库存包装（加拿大）",
  "This order has no Catalog # — Back Office must add it before the set can be stored. Keep the box aside; the confirm is locked.": "此订单没有目录编号 — 后台需先添加，套件才能入库。请将箱子放在一旁；确认按钮已锁定。",
  "Finish packing / mark the order received first — the confirm unlocks then.": "请先完成包装 / 标记已收货 — 之后确认按钮才会解锁。",
  "STOP — confirm storage": "停止 — 确认入库",
  "STOP pending": "待停止",
  "Confirm where the cancelled set is stored": "确认已取消套件的存放位置",
  "STOP appears after packing / receiving": "包装 / 收货后会出现停止提示",
  "Stored in": "已存入",
  "order moved to Cancelled Orders": "订单已移至已取消订单",
  "waiting for": "等待",
  "to store it": "入库",
  "to put it in spare parts": "放入备件",
  "confirmed by": "确认方",
  "on the printed label": "在打印的标签上",
  "Do NOT ship it. Print the label (Catalog # + colour), stick it on the box": "请勿发货。打印标签（目录编号 + 颜色），贴在箱子上",
  "Print label": "打印标签",
  "Label with": "标签含",
  "printed and stuck on the box": "已打印并贴在箱子上",
  "is on the label": "已在标签上",
  "Label sent to the printer window": "标签已发送到打印窗口",
  "Popup blocked — allow popups for this site, then print again": "弹窗被拦截 — 请允许此网站的弹窗后重新打印",
  // -------------- 🔔 Reminders square --------------
  "Reminders": "提醒",
  "press to see the list": "点击查看列表",
  "Reminders addressed to your role that are due. ✓ Done removes one from your count.": "发给您角色且已到期的提醒。点击 ✓ 完成后计数减一。",
  "Nothing due — all reminders finished.": "没有到期提醒 — 全部已完成。",
  "due": "到期",
  "set by": "设置人",
  "Done": "完成",
  "Reminder finished": "提醒已完成",
  "NA orders cancelled in Shopify": "在 Shopify 中已取消的 NA 订单",
  // -------------- 🎥 claim videos / claim deep links --------------
  "Photos (optional, up to 20)": "照片（可选，最多 20 张）",
  "Videos (optional, up to 5 · 300 MB each)": "视频（可选，最多 5 个 · 每个 300 MB）",
  "Add video": "添加视频",
  "Uploading video…": "视频上传中…",
  "Videos play inside the CRM for Back Office and for the factory in China; a copy also goes to the order's Drive folder.": "视频可在 CRM 内直接播放（后台与中国工厂均可）；同时会复制一份到订单的 Drive 文件夹。",
  "Open claim": "打开索赔",
  "claim": "索赔",
};

/**
 * Return the ZH translation for `text` if we have one, otherwise the
 * original English string. Deliberately simple — no interpolation, no
 * pluralization, no ICU. Callers pass the EXACT English string.
 */
export function translate(text, lang) {
  if (lang !== "zh") return text;
  if (text === null || text === undefined) return text;
  const key = String(text);
  return ZH_TRANSLATIONS[key] ?? key;
}
